import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/context/ShopContext";
import { visibleOrdered } from "@/components/CategoryNav";
import SearchOverlay from "@/components/SearchOverlay";

const STATIC_LINKS = [
  { to: "/coming-soon", label: "Coming soon" },
  { to: "/idees", label: "Tes idées" },
  { to: "/a-propos", label: "À propos" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user, isAdmin } = useAuth();
  const { settings } = useShop() || {};
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  // Catégories pilotées depuis l'admin (onglet Catégories & Marques)
  const catLinks = visibleOrdered(settings?.categories).map((c) => ({ to: `/boutique/${c.slug}`, label: c.label }));
  const LINKS = [{ to: "/", label: "Accueil" }, ...catLinks, ...STATIC_LINKS];

  return (
    <>
      <div className="bg-kay-ink text-kay-rose text-center py-2 px-4 font-mono text-[11px] tracking-[0.25em] uppercase" data-testid="top-banner">
        Jusqu'à -50 % sur les grandes marques ✦ Livraison offerte dès {settings?.free_shipping_above ?? 75} €
      </div>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-kay-line">
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-8 h-16">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 -ml-2"
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>
          <Link to="/" data-testid="nav-logo" className="font-display text-2xl lg:text-3xl font-semibold tracking-tight">
            KAY <span className="italic text-kay-pink">BEAUTY</span>
          </Link>
          <div className="hidden lg:flex items-center gap-5">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|à|è/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition-colors hover:text-kay-pink ${
                    isActive ? "text-kay-pink font-semibold" : "text-kay-ink/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin-link" className="hidden sm:block font-mono text-[11px] uppercase tracking-widest border border-kay-ink rounded-full px-3 py-1.5 hover:bg-kay-ink hover:text-white transition-colors">
                Admin
              </Link>
            )}
            <button
              data-testid="nav-search-button"
              onClick={() => setSearchOpen(true)}
              className="p-2.5 hover:text-kay-pink transition-colors"
              aria-label="Rechercher"
            >
              <Search size={20} />
            </button>
            <button
              data-testid="nav-account-button"
              onClick={() => navigate(user ? "/compte" : "/compte")}
              className="p-2.5 hover:text-kay-pink transition-colors"
              aria-label="Compte"
            >
              <User size={20} />
            </button>
            <button
              data-testid="nav-cart-button"
              onClick={() => navigate("/panier")}
              className="p-2.5 relative hover:text-kay-pink transition-colors"
              aria-label="Panier"
            >
              <ShoppingBag size={20} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-kay-pink text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </nav>
      </header>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-kay-ink text-kay-cream flex flex-col"
          >
            <div className="flex justify-between items-center px-5 h-16 border-b border-white/10">
              <span className="font-display text-2xl">KAY <span className="italic text-kay-rosedeep">BEAUTY</span></span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2" aria-label="Fermer">
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-10 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s|à|è/g, "-")}`}
                    className="font-display text-4xl py-2 block hover:italic hover:text-kay-rosedeep transition-all"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              {isAdmin && (
                <NavLink to="/admin" onClick={() => setOpen(false)} data-testid="mobile-nav-admin" className="font-display text-4xl py-2 block text-kay-rosedeep italic">
                  Admin
                </NavLink>
              )}
            </div>
            <p className="px-8 pb-8 font-mono text-[11px] tracking-[0.3em] uppercase text-white/50">
              Les grandes marques, les petits prix
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
